import { Nullable } from '@ps/utils';
import { DEFAULT_TARGET_MARGIN } from '../constants/survey-target-margin.constants';
import { SurveyFormState } from '../types/survey-form.types';

/**
 * The service-operator pricing row above Survey Title. Only these two values
 * are editable — SCPI is derived from them and the EPC trio is inbound
 * display data, so neither is a form field.
 */
export type SurveyMarginForm = {
  buyerCpi: Nullable<number>;
  targetMargin: Nullable<number>;
  userAcceptedMargin: boolean;
};

export type SurveyMarginFormState = SurveyFormState<Nullable<SurveyMarginForm>>;

/** What a survey with no stored pricing starts at. */
export const INITIAL_SURVEY_MARGIN_FORM: SurveyMarginForm = {
  buyerCpi: null,
  targetMargin: DEFAULT_TARGET_MARGIN,
  userAcceptedMargin: false,
};

/**
 * Value equality for the two pricing fields. Used as a `computed` comparator so
 * a survey refresh that did not change the pricing cannot reset an in-progress
 * edit. Acceptance is deliberately excluded — vouching for a value the operator
 * already typed must not re-seed the control underneath them.
 */
export function isSameMarginForm(
  left: SurveyMarginForm,
  right: SurveyMarginForm,
): boolean {
  return (
    left.buyerCpi === right.buyerCpi && left.targetMargin === right.targetMargin
  );
}

/**
 * Margin values as they go out on a save. Present only when the acting user is
 * an operator impersonating a buyer with `enableTargetMargin`; absent means
 * none of the margin keys are written to the payload at all.
 */
export interface SurveyMarginSnapshot {
  /**
   * Optional because the draft is created on the first focus-out, which is
   * usually before a Buyer CPI has been typed. The margin still goes out — only
   * `offer_price` is withheld until there is a price to send.
   */
  buyerCpi: Nullable<number>;
  targetMargin: number;
  userAcceptedMargin: boolean;
}
